import { Button, Input, Text, tokens } from "@fluentui/react-components";
import { Add20Regular, Delete20Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import {
  Controller,
  useFieldArray,
  type Control,
  type FieldValues as RHFFieldValues,
} from "react-hook-form";

import type { FieldValues } from "@/features/review/fieldFormSchema";
import { formatWithThousands, parseNumeric } from "@/lib/formatNumber";

interface Props {
  control: Control<FieldValues>;
  name: string;
  sample: Record<string, unknown>;
  readOnly: boolean;
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function TableNumericCell({
  value,
  onChange,
  disabled,
}: {
  value: unknown;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const raw =
    value === null || value === undefined ? "" : String(value).replace(/,/g, "");
  const display =
    focused || parseNumeric(raw) === null ? raw : formatWithThousands(raw);
  return (
    <Input
      size="small"
      type="text"
      inputMode="decimal"
      value={display}
      onChange={(_, d) => onChange(d.value.replace(/,/g, ""))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      disabled={disabled}
      style={{ minWidth: 140 }}
    />
  );
}

/**
 * Render an array of homogeneous objects (e.g. Time Log entries) as an
 * editable table — one row per entry, one column per key.
 */
export function ArrayOfObjectsTable({
  control,
  name,
  sample,
  readOnly,
}: Props) {
  // useFieldArray wants concrete field paths; our shape is dynamic so we
  // erase the generic locally and use string-index access inside cells.
  const rhfControl = control as unknown as Control<RHFFieldValues>;
  const { fields, append, remove } = useFieldArray({
    control: rhfControl,
    name,
  });

  const columns = useMemo(() => Object.keys(sample), [sample]);
  const numericColumns = useMemo(
    () => new Set(columns.filter((c) => typeof sample[c] === "number")),
    [columns, sample],
  );

  const addRow = () => {
    const empty: Record<string, unknown> = {};
    for (const col of columns) empty[col] = "";
    append(empty);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <div
        style={{
          overflowX: "auto",
          maxWidth: "100%",
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          borderRadius: 4,
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            minWidth: "max-content",
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: "left",
                    padding: "6px 8px",
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                    color: tokens.colorNeutralForeground2,
                    fontWeight: 600,
                  }}
                >
                  {humanize(col)}
                </th>
              ))}
              {!readOnly ? (
                <th
                  aria-label="Actions"
                  style={{
                    width: 40,
                    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                  }}
                />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {fields.map((row, rowIdx) => (
              <tr key={row.id}>
                {columns.map((col) => (
                  <td
                    key={col}
                    style={{
                      padding: "4px 4px",
                      borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
                      verticalAlign: "top",
                    }}
                  >
                    <Controller
                      control={rhfControl}
                      name={`${name}.${rowIdx}.${col}`}
                      render={({ field }) => {
                        if (numericColumns.has(col)) {
                          return (
                            <TableNumericCell
                              value={field.value}
                              onChange={field.onChange}
                              disabled={readOnly}
                            />
                          );
                        }
                        const raw = field.value;
                        const str =
                          raw === null || raw === undefined ? "" : String(raw);
                        return (
                          <Input
                            size="small"
                            value={str}
                            onChange={(_, d) => field.onChange(d.value)}
                            disabled={readOnly}
                            style={{ minWidth: 140 }}
                          />
                        );
                      }}
                    />
                  </td>
                ))}
                {!readOnly ? (
                  <td
                    style={{
                      borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
                    }}
                  >
                    <Button
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      size="small"
                      aria-label="Delete row"
                      onClick={() => remove(rowIdx)}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
            {fields.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (readOnly ? 0 : 1)}
                  style={{
                    padding: 12,
                    color: tokens.colorNeutralForeground2,
                  }}
                >
                  <Text size={200}>No rows.</Text>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {!readOnly ? (
        <div>
          <Button
            appearance="subtle"
            icon={<Add20Regular />}
            size="small"
            onClick={addRow}
          >
            Add row
          </Button>
        </div>
      ) : null}
    </div>
  );
}
